import { useCallback, useMemo } from "react"
import { useScatterPlotGraphOption } from "./options"
import { useScatterPlotGraphModel } from "./model"
import { numericValueFormat } from "../../common"
import { useStackLegend } from "../../../components/stackLegend"

const PLOT_MIN_SIZE = 8
const PLOT_MAX_SIZE = 100

type useScatterPlotGraphRenderOptionProps<T> = {
	height: number
	graphOption: ReturnType<typeof useScatterPlotGraphOption<T>>
	graphData: ReturnType<typeof useScatterPlotGraphModel<T>>
}
export const useScatterPlotGraphRenderOption = <T,>({
	height,
	graphOption: {
		xValueDef, yValueDef, sizeDef, colorDef
	},
	graphData
}: useScatterPlotGraphRenderOptionProps<T>) => {
	const toolTipFormatter = useCallback(
		(obj: {
			value: number[]
		}) => {
			const value = obj.value
			return (
				`${value[4] ?? ''}<br>` +
				(xValueDef ? `${xValueDef.title} : ${numericValueFormat(value[0], 1, xValueDef?.unit)}<br>` : '') +
				(yValueDef ? `${yValueDef.title} : ${numericValueFormat(value[1], 1, yValueDef?.unit)}<br>` : '') +
				(sizeDef ? `${sizeDef.title} : ${numericValueFormat(value[2], 1, sizeDef?.unit)}<br>` : '') +
				(colorDef ? `${colorDef.title} : ${value[5]}<br>` : '')
			)
		},
		[xValueDef, yValueDef, sizeDef, colorDef]
	)

	const sizeFunction = useCallback(
		(value: number[]) => {
			const size = value[2]
			if (!size) return 0
			const maxSize = Math.max(0, graphData?.maxSize ?? 0)	// 負値の可能性を取り除く
			const minSize = Math.max(0, graphData?.minSize ?? 0)
			if (maxSize <= minSize) return PLOT_MIN_SIZE
			const maxPlot = PLOT_MAX_SIZE * height / 500
			return (((size - minSize) / (maxSize - minSize)) * (maxPlot - PLOT_MIN_SIZE)) + PLOT_MIN_SIZE
		},
		[height, graphData]
	)

	const legend = useStackLegend({ show: colorDef ? true : false })
	const series = useMemo(
		() => {
			//console.log('build series')
			return graphData?.series.map((series) => ({
				type: 'scatter',
				name: series.name,
				symbolSize: sizeFunction,
				encode: { tooltip: [0, 1] },
				blendMode: 'source-over',
				data: series.data
			})) || []
		},
		[graphData, sizeFunction]
	)

	const { xAxis, yAxis } = useMemo(
		() => {
			//console.log('build axis')
			return {
				yAxis: {
					type: 'value',
					min: graphData?.ymin,
					max: graphData?.ymax,
					axisLabel: {
						formatter: `{value} ${yValueDef?.unit ?? ''}`,
					}
				},
				xAxis: {
					type: 'value',
					min: graphData?.xmin,
					max: graphData?.xmax,
					axisLabel: {
						formatter: `{value} ${xValueDef?.unit ?? ''}`,
						align: 'right'
					}
				},
			}
		},
		[graphData, yValueDef, xValueDef]
	)
	return {
		//animation: false,
		tooltip: {
			formatter: toolTipFormatter
		},
		grid: {
			left: 20,
			right: 20,
			top: 20,
			bottom: 20,
			containLabel: true
		},
		legend,
		toolbox: {
			show: false
		},
		brush: {
			toolbox: ['rect', 'polygon', 'lineX', 'lineY'],
			brushMode: 'single',
			transformable: true
		},
		yAxis, xAxis,
		series,
		textStyle: {
			fontFamily: 'Noto Sans JP,sans-serif'
		}
	}
}
